import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { SectionCard } from '@/components/common/section-card';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/fleet/states';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePublishScreenshots } from '@/hooks/use-publish-screenshots';
import { useStoreScreenshots } from '@/hooks/use-store-screenshots';
import type { PublishOptions, StorePlatform, StoreScreenshot } from '@/types/store';

import { notifyPublishError } from './publish-feedback';
import { PublishConfirmDialog } from './publish-confirm-dialog';
import { ScreenshotSlotDialog } from './screenshot-slot-dialog';
import { ListingStatusBadge } from './store-badges';
import { PLATFORM_LABELS } from './store-constants';

/** Groups slots by locale, preserving the API's platform/locale/type/position order. */
function groupByLocale(rows: StoreScreenshot[]): Map<string, StoreScreenshot[]> {
  const groups = new Map<string, StoreScreenshot[]>();

  for (const row of rows) {
    const list = groups.get(row.locale) ?? [];

    list.push(row);
    groups.set(row.locale, list);
  }

  return groups;
}

/** Screenshot slots grouped by locale, with add/edit + per-locale publish. */
export function ScreenshotsPanel({ platform }: { platform: StorePlatform }): JSX.Element {
  const { data, isLoading, isError, error, refetch } = useStoreScreenshots();
  const publish = usePublishScreenshots();

  const [slotDialog, setSlotDialog] = useState<{ slot?: StoreScreenshot } | null>(null);
  const [publishLocale, setPublishLocale] = useState<string | null>(null);

  const rows = useMemo(
    () => (data ?? []).filter((s) => s.platform === platform),
    [data, platform],
  );
  const groups = useMemo(() => groupByLocale(rows), [rows]);
  const publishCount = publishLocale ? (groups.get(publishLocale)?.length ?? 0) : 0;

  function confirmPublish(options: PublishOptions): void {
    if (!publishLocale) return;
    const locale = publishLocale;

    publish.mutate(
      { platform, locale, options },
      {
        onSuccess: (published) => {
          toast.success(
            `Published ${published.length} screenshot${published.length === 1 ? '' : 's'} · ${locale}.`,
          );
          setPublishLocale(null);
        },
        onError: notifyPublishError,
      },
    );
  }

  const action = (
    <Button size="sm" onClick={() => setSlotDialog({})}>
      <Plus className="h-4 w-4" aria-hidden />
      Add slot
    </Button>
  );

  return (
    <SectionCard
      title="Screenshots"
      description="Slots reference images by their R2 object key. Publishing replaces all screenshots of a type for the locale."
      action={action}
    >
      {isLoading && <TableSkeleton rows={3} label="Loading screenshots" />}

      {!isLoading && isError && !data && (
        <ErrorState
          title="Couldn't load screenshots."
          error={error}
          onRetry={() => void refetch()}
        />
      )}

      {!isLoading && data && rows.length === 0 && (
        <EmptyState message={`No ${PLATFORM_LABELS[platform]} screenshots yet`} action={action} />
      )}

      {!isLoading && rows.length > 0 && (
        <div className="space-y-4">
          {[...groups.entries()].map(([locale, slots]) => (
            <div key={locale} className="overflow-hidden rounded-md border border-border">
              <div className="flex items-center justify-between gap-2 border-b border-border bg-secondary/30 px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm text-foreground">{locale}</span>
                  <span className="text-xs text-muted-foreground">
                    {slots.length} slot{slots.length === 1 ? '' : 's'}
                  </span>
                </div>
                <Button size="sm" onClick={() => setPublishLocale(locale)}>
                  Publish
                </Button>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Image type</TableHead>
                    <TableHead>Pos</TableHead>
                    <TableHead>R2 object key</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {slots.map((slot) => (
                    <TableRow key={slot.id}>
                      <TableCell className="font-mono">{slot.imageType}</TableCell>
                      <TableCell className="font-mono">{slot.position}</TableCell>
                      <TableCell className="max-w-xs truncate font-mono text-xs text-muted-foreground">
                        {slot.objectKey}
                      </TableCell>
                      <TableCell>
                        <ListingStatusBadge status={slot.status} />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSlotDialog({ slot })}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ))}
        </div>
      )}

      <ScreenshotSlotDialog
        open={slotDialog !== null}
        onOpenChange={(open) => !open && setSlotDialog(null)}
        platform={platform}
        slot={slotDialog?.slot}
      />

      <PublishConfirmDialog
        open={publishLocale !== null}
        onOpenChange={(open) => !open && setPublishLocale(null)}
        platform={platform}
        target={`${publishCount} ${publishLocale ?? ''} screenshot${publishCount === 1 ? '' : 's'}`}
        isPending={publish.isPending}
        onConfirm={confirmPublish}
      />
    </SectionCard>
  );
}
